// ProtectedRoute.jsx
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@context/AuthContext';
import Loading from './Loading';

/**
 * Inline-style ProtectedRoute
 * - Preserves same API as before:
 *   requiredRole, requiredPermission, moduleId, branchId
 * - Uses inline styles + CSS variables to match the rest of the app's visual language
 */

const IconWrapper = ({ bg, strokeColor, children }) => (
  <div
    style={{
      width: 64,
      height: 64,
      borderRadius: 9999,
      background: bg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 16px auto'
    }}
    aria-hidden
  >
    <svg
      width="32"
      height="32"
      viewBox="0 0 24 24"
      fill="none"
      stroke={strokeColor}
      xmlns="http://www.w3.org/2000/svg"
    >
      {children}
    </svg>
  </div>
);

const CenteredCard = ({ children }) => (
  <div
    style={{
      minHeight: '60vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem'
    }}
  >
    <div
      style={{
        textAlign: 'center',
        maxWidth: 640,
        width: '100%'
      }}
    >
      {children}
    </div>
  </div>
);

const ProtectedRoute = ({
  children,
  requiredRole = null,
  requiredPermission = null,
  moduleId = null,
  branchId = null
}) => {
  const { user, isAuthenticated, isLoading, hasRole, hasPermission } = useAuth();
  const location = useLocation();

  // Loading state while checking auth
  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loading size="lg" text="Checking authentication..." />
      </div>
    );
  }

  // Not authenticated -> redirect to login (preserve attempted location)
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Access denied due to role
  if (requiredRole && !hasRole(requiredRole)) {
    return (
      <CenteredCard>
        <IconWrapper bg="rgba(254,226,226,0.9)" strokeColor="rgb(220,38,38)">
          {/* Exclamation inside triangle (error icon) */}
          <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </IconWrapper>

        <h2 style={{ fontSize: '1.5rem', margin: '0 0 8px 0', fontWeight: 700, color: 'var(--gray-900, #0f172a)' }}>
          Access Denied
        </h2>

        <p style={{ margin: '0 0 16px 0', color: 'var(--gray-600, #6b7280)' }}>
          You don't have permission to access this page.
        </p>

        <div style={{ fontSize: 14, color: 'var(--gray-500, #6b7280)' }}>
          <div style={{ marginBottom: 6 }}>
            <strong style={{ color: 'var(--gray-900)', marginRight: 6 }}>Required role:</strong>
            <span style={{ textTransform: 'capitalize' }}>{requiredRole}</span>
          </div>
          <div>
            <strong style={{ color: 'var(--gray-900)', marginRight: 6 }}>Your role:</strong>
            <span style={{ textTransform: 'capitalize' }}>{user.role}</span>
          </div>
        </div>
      </CenteredCard>
    );
  }

  // Insufficient permission
  if (requiredPermission && !hasPermission(requiredPermission, moduleId, branchId)) {
    return (
      <CenteredCard>
        <IconWrapper bg="rgba(255,249,230,0.95)" strokeColor="rgb(168,85,0)">
          {/* Info / warning icon */}
          <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </IconWrapper>

        <h2 style={{ fontSize: '1.5rem', margin: '0 0 8px 0', fontWeight: 700, color: 'var(--gray-900, #0f172a)' }}>
          Insufficient Permissions
        </h2>

        <p style={{ margin: '0 0 16px 0', color: 'var(--gray-600, #6b7280)' }}>
          You don't have the required permissions to perform this action.
        </p>

        <div style={{ fontSize: 14, color: 'var(--gray-500, #6b7280)' }}>
          <div>
            <strong style={{ color: 'var(--gray-900)', marginRight: 6 }}>Required permission:</strong>
            <span>{requiredPermission}</span>
          </div>
        </div>
      </CenteredCard>
    );
  }

  // All checks passed -> render children
  return children;
};

export default ProtectedRoute;
